import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Code2,
  Database,
  Globe2,
  Layers3,
  Rocket,
  ShieldCheck,
  Sparkles,
  Terminal,
  Users,
  BriefcaseBusiness,
  Clock3,
  Award,
  ChevronDown,
} from "lucide-react";

import { softwareEngPageFirstImg } from "../../assets";
import Card from "../../components/card";
import SecondCard from "../../components/courses-second-card";
import Layout from "../../components/shared/layout/layout";
import CourseDescription from "../cyber-security/component/course-description";

const SoftwareEngineering = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 35 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  const features = [
    {
      icon: Code2,
      title: "Build Real Products",
      text: "Move beyond tutorials. Build production-style applications that solve real problems.",
    },
    {
      icon: Layers3,
      title: "Full-Stack Thinking",
      text: "Understand how frontend, backend, databases and APIs work together.",
    },
    {
      icon: Rocket,
      title: "Career Focused",
      text: "Develop practical engineering skills designed around real-world opportunities.",
    },
    {
      icon: Users,
      title: "Mentorship",
      text: "Learn with guidance, feedback and support from experienced practitioners.",
    },
  ];

  const curriculum = [
    "HTML, CSS & Modern JavaScript",
    "React & Modern Frontend Engineering",
    "Node.js & Backend Development",
    "REST APIs & Application Architecture",
    "SQL & Database Design",
    "Git & Collaborative Development",
    "Authentication & Security",
    "Cloud Deployment",
    "Testing & Debugging",
    "Real-World Capstone Projects",
    "Python developments and Django",
    "Computer science fundamentals"
  ];

  const careerPaths = [
    "Frontend Engineer",
    "Backend Engineer",
    "Full-Stack Engineer",
    "Software Developer",
    "Web Application Engineer",
    "Junior DevOps / Cloud Engineer",
  ];

  return (
    <Layout>
      <div className="bg-white text-[#07141a] overflow-hidden">

        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="relative min-h-[760px] bg-[#07141a] overflow-hidden">

          {/* Background glow */}
          <div className="absolute inset-0 pointer-events-none">

            <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[#59b5cd]/20 blur-[130px] rounded-full" />

            <div className="absolute top-40 right-[-180px] w-[600px] h-[600px] bg-[#ecb32c]/10 blur-[150px] rounded-full" />

            <div className="absolute bottom-[-200px] left-1/3 w-[500px] h-[400px] bg-[#59b5cd]/10 blur-[140px] rounded-full" />

          </div>

          {/* Grid */}
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "linear-gradient(#59b5cd 1px, transparent 1px), linear-gradient(90deg, #59b5cd 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">

            <div className="grid lg:grid-cols-2 gap-16 items-center">

              {/* LEFT */}
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
              >

                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#59b5cd]/30 bg-[#59b5cd]/10 text-[#59b5cd] text-sm font-semibold mb-7">
                  <Sparkles size={16} />
                  Zulfah Academy · Software Engineering
                </div>

                <h1 className="text-5xl sm:text-6xl lg:text-[76px] font-black leading-[0.98] tracking-tight text-white">

                  Build.
                  <br />

                  <span className="text-[#59b5cd]">
                    Engineer.
                  </span>

                  <br />

                  <span className="text-[#ecb32c]">
                    Launch.
                  </span>

                </h1>

                <p className="text-lg md:text-xl text-white/65 max-w-xl mt-8 leading-relaxed">
                  Learn how to design, build and deploy modern software
                  applications — from your first line of code to production.
                </p>

                <div className="flex flex-wrap gap-4 mt-9">

                  <Link to="/get-started">

                    <button className="group flex items-center gap-3 bg-[#ecb32c] hover:bg-[#f2c34e] text-[#07141a] px-7 py-4 rounded-xl font-bold transition-all shadow-lg shadow-[#ecb32c]/20">

                      Start Your Journey

                      <ArrowRight
                        size={20}
                        className="group-hover:translate-x-1 transition"
                      />

                    </button>

                  </Link>

                  <a href="#curriculum">

                    <button className="flex items-center gap-2 px-7 py-4 rounded-xl border border-white/15 bg-white/5 text-white hover:bg-white/10 transition font-semibold">

                      Explore Curriculum

                      <ChevronDown size={18} />

                    </button>

                  </a>

                </div>

                {/* mini stats */}

                <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/10">

                  <div>
                    <p className="text-2xl font-bold text-white">
                      4 Months
                    </p>

                    <p className="text-sm text-white/40">
                      Intensive Program
                    </p>
                  </div>

                  <div>
                    <p className="text-2xl font-bold text-white">
                      Beginner
                    </p>

                    <p className="text-sm text-white/40">
                      Friendly
                    </p>
                  </div>

                  <div>
                    <p className="text-2xl font-bold text-white">
                      Real Projects
                    </p>

                    <p className="text-sm text-white/40">
                      Portfolio Ready
                    </p>
                  </div>

                </div>

              </motion.div>


              {/* RIGHT VISUAL */}

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                }}
                transition={{ duration: 0.9 }}
                className="relative"
              >

                {/* Main image */}

                <div className="relative z-10 rounded-[30px] border border-white/10 bg-white/5 backdrop-blur-xl p-3 shadow-2xl">

                  <div className="relative overflow-hidden rounded-[22px]">

                    <img
                      src={softwareEngPageFirstImg}
                      alt="Software Engineering at Zulfah Academy"
                      className="w-full h-[430px] object-cover"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-[#07141a]/80 via-transparent to-transparent" />

                    <div className="absolute bottom-6 left-6 right-6">

                      <div className="flex items-center gap-3">

                        <div className="w-11 h-11 rounded-xl bg-[#ecb32c] flex items-center justify-center text-[#07141a]">
                          <Terminal size={22} />
                        </div>

                        <div>
                          <p className="text-white font-bold">
                            Engineering Mode
                          </p>

                          <p className="text-white/50 text-sm">
                            From idea → production
                          </p>
                        </div>

                      </div>

                    </div>

                  </div>

                </div>


                {/* Floating card */}

                <motion.div
                  animate={{ y: [0, -12, 0] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute -left-8 bottom-10 z-20 bg-white rounded-2xl p-4 shadow-2xl hidden md:flex items-center gap-3"
                >

                  <div className="w-11 h-11 rounded-xl bg-[#59b5cd]/15 flex items-center justify-center">
                    <Award
                      size={22}
                      className="text-[#076c93]"
                    />
                  </div>

                  <div>

                    <p className="font-bold text-[#07141a]">
                      Industry Ready
                    </p>

                    <p className="text-xs text-gray-500">
                      Learn by building
                    </p>

                  </div>

                </motion.div>


                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute -right-8 top-12 z-20 bg-[#ecb32c] rounded-2xl p-4 shadow-xl hidden md:flex items-center gap-3"
                >

                  <Code2 size={25} />

                  <div>

                    <p className="font-bold">
                      Build Real Apps
                    </p>

                    <p className="text-xs opacity-70">
                      Not just tutorials
                    </p>

                  </div>

                </motion.div>

              </motion.div>

            </div>

          </div>

        </section>


        {/* =====================================================
            COURSE SNAPSHOT
        ===================================================== */}

        <section className="relative -mt-12 z-20 px-6">

          <div className="max-w-6xl mx-auto">

            <div className="grid grid-cols-2 md:grid-cols-4 bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">

              {[
                {
                  icon: Clock3,
                  title: "4 Months",
                  text: "Duration",
                },
                {
                  icon: Terminal,
                  title: "Beginner",
                  text: "Level",
                },
                {
                  icon: BriefcaseBusiness,
                  title: "Projects",
                  text: "Practical Learning",
                },
                {
                  icon: Award,
                  title: "Certificate",
                  text: "Upon Completion",
                },
              ].map((item, index) => {

                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="p-6 md:p-8 flex items-center gap-4 border-b md:border-b-0 md:border-r last:border-0 border-gray-100"
                  >

                    <div className="w-11 h-11 rounded-xl bg-[#59b5cd]/10 flex items-center justify-center shrink-0">

                      <Icon
                        size={21}
                        className="text-[#076c93]"
                      />

                    </div>

                    <div>

                      <p className="font-bold text-lg">
                        {item.title}
                      </p>

                      <p className="text-sm text-gray-500">
                        {item.text}
                      </p>

                    </div>

                  </div>
                );

              })}

            </div>

          </div>

        </section>


        {/* =====================================================
            INTRO
        ===================================================== */}

        <section className="max-w-7xl mx-auto px-6 py-28">

          <motion.div
            variants={fadeUp}
            whileInView="visible"
            initial="hidden"
            viewport={{ once: true }}
            className="max-w-4xl"
          >

            <p className="uppercase tracking-[0.25em] text-[#076c93] text-sm font-bold mb-4">
              Why Software Engineering?
            </p>

            <h2 className="text-4xl md:text-6xl font-black leading-tight">

              Don't just learn to code.

              <span className="block text-[#59b5cd]">
                Learn to engineer.
              </span>

            </h2>

            <p className="text-gray-600 text-lg leading-relaxed mt-7 max-w-3xl">

              Software engineering is more than writing code. It is the
              discipline of transforming ideas into reliable, scalable and
              maintainable technology products.

            </p>

          </motion.div>


          {/* Features */}

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-16">

            {features.map((feature, index) => {

              const Icon = feature.icon;

              return (
                <motion.div
                  key={index}
                  variants={fadeUp}
                  whileInView="visible"
                  initial="hidden"
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group p-7 rounded-3xl border border-gray-100 bg-white hover:border-[#59b5cd]/40 hover:shadow-xl transition-all"
                >

                  <div className="w-12 h-12 rounded-xl bg-[#59b5cd]/10 flex items-center justify-center mb-6 group-hover:bg-[#ecb32c] transition">

                    <Icon size={23} />

                  </div>

                  <h3 className="text-xl font-bold">
                    {feature.title}
                  </h3>

                  <p className="text-gray-500 text-sm leading-relaxed mt-3">
                    {feature.text}
                  </p>

                </motion.div>
              );

            })}

          </div>

        </section>


        {/* =====================================================
            DARK BREAK SECTION
        ===================================================== */}

        <section className="bg-[#07141a] relative overflow-hidden">

          <div className="absolute w-[500px] h-[500px] bg-[#59b5cd]/10 rounded-full blur-[130px] -left-40 top-0" />

          <div className="max-w-7xl mx-auto px-6 py-28 relative">

            <div className="grid lg:grid-cols-2 gap-16 items-center">

              <div>

                <p className="text-[#ecb32c] uppercase tracking-[0.25em] font-bold text-sm">
                  The Zulfah Approach
                </p>

                <h2 className="text-white text-4xl md:text-6xl font-black mt-5 leading-tight">

                  Learn by
                  <span className="text-[#59b5cd]">
                    {" "}building.
                  </span>

                </h2>

                <p className="text-white/55 text-lg mt-7 leading-relaxed max-w-xl">

                  Every concept should lead somewhere. Instead of spending
                  months watching tutorials, you'll apply what you learn
                  through practical exercises and real-world projects.

                </p>

                <div className="mt-9 space-y-4">

                  {[
                    "Hands-on engineering exercises",
                    "Real-world application projects",
                    "Code reviews & mentorship",
                    "Portfolio development",
                  ].map((item, index) => (

                    <div
                      key={index}
                      className="flex items-center gap-3 text-white"
                    >

                      <CheckCircle2
                        size={20}
                        className="text-[#ecb32c]"
                      />

                      <span>
                        {item}
                      </span>

                    </div>

                  ))}

                </div>

              </div>


              {/* Code Window */}

              <div className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl overflow-hidden shadow-2xl">

                <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10">

                  <span className="w-3 h-3 rounded-full bg-red-400" />
                  <span className="w-3 h-3 rounded-full bg-yellow-400" />
                  <span className="w-3 h-3 rounded-full bg-green-400" />

                  <span className="ml-3 text-white/30 text-xs">
                    zulfah-app.js
                  </span>

                </div>

                <div className="p-7 font-mono text-sm leading-8">

                  <p className="text-[#59b5cd]">
                    const
                    <span className="text-white"> learner </span>
                    =
                    <span className="text-[#ecb32c]"> newEngineer()</span>;
                  </p>

                  <p className="text-white/50">
                    {"// Build. Learn. Iterate."}
                  </p>

                  <p className="text-[#59b5cd]">
                    learner.<span className="text-white">learn</span>();
                  </p>

                  <p className="text-[#59b5cd]">
                    learner.<span className="text-white">build</span>();
                  </p>

                  <p className="text-[#59b5cd]">
                    learner.<span className="text-white">ship</span>();
                  </p>

                  <p className="text-[#ecb32c]">
                    learner.<span className="text-white">grow</span>();
                  </p>

                  <div className="mt-6 border-t border-white/10 pt-5 text-white/30">
                    // Your journey starts here.
                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CURRICULUM
        ===================================================== */}

        <section
          id="curriculum"
          className="max-w-7xl mx-auto px-6 py-28"
        >

          <div className="text-center max-w-3xl mx-auto">

            <p className="text-[#076c93] uppercase tracking-[0.25em] font-bold text-sm">
              What You'll Learn
            </p>

            <h2 className="text-4xl md:text-6xl font-black mt-4">
              Your engineering
              <span className="text-[#ecb32c]">
                {" "}toolkit.
              </span>
            </h2>

            <p className="text-gray-500 mt-6 text-lg">
              A practical curriculum built around the technologies and
              engineering concepts modern developers use every day.
            </p>

          </div>


          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-16">

            {curriculum.map((item, index) => (

              <motion.div
                key={index}
                whileHover={{ y: -4 }}
                className="flex items-center gap-4 p-5 rounded-2xl bg-[#f8fbfc] border border-gray-100 hover:border-[#59b5cd]/40 transition"
              >

                <div className="w-9 h-9 rounded-full bg-[#59b5cd]/10 flex items-center justify-center shrink-0">

                  <CheckCircle2
                    size={18}
                    className="text-[#076c93]"
                  />

                </div>

                <span className="font-semibold text-gray-800">
                  {item}
                </span>

              </motion.div>

            ))}

          </div>

        </section>


        {/* =====================================================
            CAREER PATHS
        ===================================================== */}

        <section className="bg-[#f6fafb]">

          <div className="max-w-7xl mx-auto px-6 py-28">

            <div className="grid lg:grid-cols-2 gap-16 items-center">

              <div>

                <p className="text-[#076c93] uppercase tracking-[0.25em] font-bold text-sm">
                  Where It Can Take You
                </p>

                <h2 className="text-4xl md:text-5xl font-black mt-4">

                  One foundation.
                  <span className="block text-[#59b5cd]">
                    Multiple career paths.
                  </span>

                </h2>

                <p className="text-gray-600 text-lg leading-relaxed mt-6">

                  Software engineering gives you a foundation that can open
                  doors across startups, financial technology, enterprise
                  software, healthcare, logistics and more.

                </p>

                <Link to="/get-started">

                  <button className="mt-8 flex items-center gap-3 bg-[#07141a] text-white px-7 py-4 rounded-xl font-bold hover:bg-[#076c93] transition">

                    Begin Your Journey

                    <ArrowRight size={19} />

                  </button>

                </Link>

              </div>


              <div className="grid sm:grid-cols-2 gap-4">

                {careerPaths.map((career, index) => (

                  <div
                    key={index}
                    className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition"
                  >

                    <div className="w-10 h-10 rounded-xl bg-[#ecb32c]/15 flex items-center justify-center mb-5">

                      <BriefcaseBusiness
                        size={20}
                        className="text-[#a87500]"
                      />

                    </div>

                    <h3 className="font-bold">
                      {career}
                    </h3>

                    <p className="text-xs text-gray-400 mt-2">
                      Career pathway
                    </p>

                  </div>

                ))}

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            EXISTING COURSE INFORMATION
        ===================================================== */}

        <section className="max-w-7xl mx-auto px-6 py-28">

          <div className="text-center mb-16">

            <p className="text-[#076c93] uppercase tracking-[0.25em] font-bold text-sm">
              Program Details
            </p>

            <h2 className="text-4xl md:text-5xl font-black mt-4">
              Everything you need
              <span className="text-[#ecb32c]">
                {" "}to get started.
              </span>
            </h2>

          </div>


          <div className="grid lg:grid-cols-1 gap-8 items-start">

           

              <div className="rounded-3xl bg-white border border-gray-100 shadow-sm p-6 md:p-10">

                <CourseDescription />

              </div>

            



          </div>

        </section>


        {/* =====================================================
            FINAL CTA
        ===================================================== */}

        <section className="relative bg-[#07141a] overflow-hidden">

          <div className="absolute inset-0">

            <div className="absolute w-[500px] h-[500px] bg-[#59b5cd]/15 blur-[140px] rounded-full -left-40 -top-40" />

            <div className="absolute w-[450px] h-[450px] bg-[#ecb32c]/10 blur-[140px] rounded-full -right-40 -bottom-40" />

          </div>

          <div className="relative max-w-5xl mx-auto px-6 py-28 text-center">

            <div className="inline-flex items-center gap-2 text-[#ecb32c] font-semibold mb-6">

              <Rocket size={19} />

              Your next chapter starts here.

            </div>

            <h2 className="text-4xl md:text-6xl font-black text-white leading-tight">

              Ready to become
              <span className="text-[#59b5cd]">
                {" "}a software engineer?
              </span>

            </h2>

            <p className="text-white/55 text-lg max-w-2xl mx-auto mt-6">

              Stop waiting for the perfect time. Start building the skills,
              projects and confidence needed to enter the technology industry.

            </p>

            <div className="flex justify-center flex-wrap gap-4 mt-9">

              <Link to="/get-started">

                <button className="flex items-center gap-3 bg-[#ecb32c] text-[#07141a] px-8 py-4 rounded-xl font-bold hover:scale-[1.03] transition shadow-xl">

                  Apply Now

                  <ArrowRight size={20} />

                </button>

              </Link>

              <a href="#curriculum">

                <button className="px-8 py-4 rounded-xl border border-white/15 bg-white/5 text-white font-semibold hover:bg-white/10 transition">

                  View Curriculum

                </button>

              </a>

            </div>

          </div>

        </section>

      </div>
    </Layout>
  );
};

export default SoftwareEngineering;